import styled, { css } from 'styled-components';
import { Link as GatsbyLink } from 'gatsby';
import { Colors } from './utils/colors';
import { ColorTheme } from './utils/colorTheme';
import { Cormorant } from './utils/font';

export const Container = styled.main`
    max-width: 1440px;
    width: 100%;
    margin: 0 auto;
    padding: 0 20px;
    box-sizing: border-box;
    display: flex;
`;

export const Paragraph = styled.p`
    line-height: 1.5em;
    font-size: 1.2em;

    &:not(:last-child) {
        margin-bottom: 30px;
    }
`;

export const Text = styled.p`
    max-width: 600px;
    text-align: justify;
    line-height: 1.2em;

    &:not(:last-child) {
        margin-bottom: 30px;
    }
`;

export const WideText = styled.p`
    text-align: justify;
    line-height: 1.2em;
    &:not(:last-child) {
        margin-bottom: 30px;
    }
`;

export const Link = styled(GatsbyLink)`
    color: inherit;
    text-decoration: none;

    &:hover {
        color: ${Colors.blue}
    }
`;

export const List = styled.ol`
  list-style: none;
  padding-left: 20px;
  margin-bottom: 60px;

  li {
    margin-top: 10px;
  }
`;

export const TextButton = styled.button`
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  color: ${Colors.blue};

  &:hover {
    color: ${Colors.sea};
    cursor: pointer;
    text-decoration: underline;
  }
`;

export const NumberedList = styled(List)`
  list-style: numbered;
`;

export const HTML = styled.article<{ fullWidth?: boolean }>`
    max-width: 600px;
    text-align: justify;
    width: 100%;
    display: flex;
    flex-direction: column;

  p {
    line-height: 1.5em;
    font-size: 1.2em;

    &:not(:last-child) {
      margin-bottom: 30px;
    }
  }

  ul {
    margin-left: 20px;
    padding: 0;

    li {
      list-style: disc;
      margin-bottom: 20px;
    }

  }

  strong {
    font-weight: bold;
  }

  em {
    font-style: italic;
  }

  a {
      text-decoration: none;

      &:hover {
          text-decoration: underline;
      }
    }
  
  ${(p) => p.fullWidth && css`
    max-width: none;

    p {
      max-width: none;
    }
  `}
`;


export const Em = styled.em`
  font-style: italic;
`;

export const H1 = styled.h1`
  font-size: 4em;
  margin-bottom: 30px;
  display: block;


  small {
    display: block;
    font-size: 0.6em;
    text-align: left !important;
  }
`;

export const H2 = styled.h2<{ leftAlign?: boolean }>`
  font-size: 3em;
  margin-bottom: 30px;
  display: inline-block;

  ${(p) => p.leftAlign && css`
    text-align: left !important;
  `}

    em {
      font-style: italic;
    }
`;

export const H3 = styled.h3`
  font-size: 2em;
  margin-bottom: 30px;
  display: inline-block;
`;

export const H6 = styled.h3`
  font-size: 2em;
  margin-bottom: 30px;
  display: inline-block;
  text-transform: uppercase;
`;

export const Img = styled.img`
    width: 100%;
`;

export const Figure = styled.figure``;

export const Caption = styled.figcaption`
  color: ${Colors.white};
  font-style: italic;
  margin-top: 40px;
  max-width: 400px;
  line-height: 1.4em;
`;

const getButtonStyle = (theme: ColorTheme) => {
    switch(theme) {
      case 'navy':
        return css`
          background-color: ${Colors.lightGold};
          color: ${Colors.navy} !important;

          &:hover {
            background-color: ${Colors.gold};
          }
        `
      case 'gold':
        return css`
          background-color: ${Colors.sea};
          color: ${Colors.lightGold} !important;

          &:hover {
            background-color: ${Colors.navy};
          }
        `
      default:
        return css`
          background-color: ${Colors.red};
          color: ${Colors.white};

          &:hover {
            background-color: ${Colors.gold};
          }
        `
    }
}

const ButtonStyle = (theme?: ColorTheme) => {
  return css`
    font-size: 2em;
    padding: 10px 40px;
    position: relative;
    text-decoration: none;
    margin-top: 10px;
    display: inline-block;

    &:hover {
      cursor: pointer;
      text-decoration: none !important;
    }

    &:disabled {
      opacity: 0.1;
      cursor: not-allowed;
    }

    ${theme && getButtonStyle(theme)}
  `
}

export const Button = styled(GatsbyLink)<{ theme?: ColorTheme }>`
  ${(p) => ButtonStyle(p.theme)};
  align-self: start;
`;

export const FormButton = styled.button`
  ${ButtonStyle('gold')}
`;

export const Select = styled.select`
  margin-bottom: 10px;
  max-width: fit-content;
  ${Cormorant};
  font-size: 1.4em;
  height: 60px;
  padding: 0 20px;
  box-sizing: content-box;
`;

export const Label = styled.label`
  margin-bottom: 10px;
  font-size: 1.2em;
`;

export const Table = styled.table`
  max-width: 340px;
  margin-bottom: 40px;
  width: 100%;
`;

export const TableRow = styled.tr`
  padding-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const TableCell = styled.td``;

export const Price = styled(TableCell)`
  font-family: sans-serif;
  font-weight: 400;
`;

export const Form = styled.form`
  margin-top: 20px;
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
`;

export const PayPalButton = styled.input``;

export const Quote = styled.blockquote<{ theme?: ColorTheme, goBig?: boolean }>`
    font-size: 3em;
    color: ${(p) => p.theme === 'navy' ? Colors.white : Colors.black};
    font-style: italic;

    ${(p) => p.goBig && css`
      font-size: 4em;
    `}

    @media screen and (max-width: 900px){
      font-size: 2.5em !important;
    }

    @media screen and (max-width: 600px){
      font-size: 2em !important;
    }

    &:before, &:after{
        content: '"';
    }
`;

export const Input = styled.input`
  height: 50px;
  font-size: 1em;
  font-family: inherit;
  padding: 0 15px;
  display: flex;
  margin-bottom: 15px;
  border-radius: 0;
  border: none;

  &:active {
    outline-color: ${Colors.navy};
  }
`;

export const VideoHolder = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 Aspect Ratio */
  height: 0;
  overflow: hidden;
  margin-bottom: 60px;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

export const Checkbox = styled.input`
  margin-right: 20px;
  cursor: pointer;
`;

export const CheckboxLabel = styled.label`
  cursor: pointer;
`;

export const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 10px;
  margin-top: 20px;
  align-items: center;
`;